
import React, { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { fr, enUS, es } from "date-fns/locale";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDeletePuttingResult } from "@/hooks/useDeletePuttingResult";

interface PuttingHistoryTabsProps {
  puttingResults: any[];
  level1Results: any[];
  level2Results: any[];
  level3Results: any[];
  onDelete?: (id: string) => void;
  onLevelChange?: (level: string | null) => void;
}

const PuttingHistoryTabs: React.FC<PuttingHistoryTabsProps> = ({
  puttingResults,
  level1Results,
  level2Results,
  level3Results,
  onDelete,
  onLevelChange
}) => {
  const { t, i18n } = useTranslation('common');
  const { deletePuttingResult } = useDeletePuttingResult();

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'en':
        return enUS;
      case 'es':
        return es;
      default:
        return fr;
    }
  };

  const getDateFormat = () => {
    switch (i18n.language) {
      case 'en':
        return "MMMM d, yyyy HH:mm";
      case 'es':
        return "d 'de' MMMM 'de' yyyy HH:mm";
      default:
        return "d MMMM yyyy HH:mm";
    }
  };

  // Debug logs to diagnose the issue
  useEffect(() => {
    console.log('PuttingHistoryTabs: Results received:');
    console.log('All results:', puttingResults.length);
    console.log('Level 1 results:', level1Results.length);
    console.log('Level 2 results:', level2Results.length);
    console.log('Level 3 results:', level3Results.length);
  }, [puttingResults, level1Results, level2Results, level3Results]);

  // Use the provided onDelete function or fallback to the hook's implementation
  const handleDelete = async (id: string) => {
    if (onDelete) {
      onDelete(id);
    } else {
      await deletePuttingResult(id);
    }
  };

  // Handle tab change to notify parent
  const handleTabChange = (value: string) => {
    console.log(`PuttingHistoryTabs: Tab changed to: ${value}`);
    
    if (onLevelChange) {
      if (value === "all") {
        onLevelChange(null);
      } else if (value === "level1") {
        onLevelChange("1");
      } else if (value === "level2") {
        onLevelChange("2");
      } else if (value === "level3") {
        onLevelChange("3");
      }
    }
  };

  // Render a single result item as a collapsible element
  const renderResultItem = (result: any) => {
    const formattedDate = format(
      new Date(result.test_date), 
      getDateFormat(), 
      { locale: getDateLocale() }
    );
    
    return (
      <div key={result.id} className="border-b border-gray-200 py-2">
        <Collapsible className="w-full">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger className="flex flex-1 items-center justify-between py-2 text-left font-medium hover:underline focus:outline-none">
              <div className="flex flex-1 items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-golf-700">{formattedDate}</span>
                   <span className="text-muted-foreground">
                    {result.test_level ? `${t('puttingTraining.history.level')} ${result.test_level}` : 'Standard'}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="font-semibold text-golf-600">{result.points} pts</span>
                </div>
              </div>
            </CollapsibleTrigger>
          </div>
          <CollapsibleContent className="px-4 py-2 bg-muted/20 rounded-md mt-1 mb-2">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">{t('puttingTraining.history.training')} {result.test_name || t('puttingTraining.history.standardTraining')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('puttingTraining.history.score')} <span className="font-medium">{result.points} points</span>
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(result.id)}
                className="hover:bg-destructive/20 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  };

  // Render a section of results
  const renderResultsSection = (results: any[]) => {
    if (!results || results.length === 0) {
      return <div className="text-center py-4 text-muted-foreground">{t('puttingTraining.history.noResults')}</div>;
    }

    return (
      <div className="space-y-1">
        {results.map(renderResultItem)}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-xl text-golf-700">{t('puttingTraining.history.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" onValueChange={handleTabChange}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">{t('puttingTraining.history.tabs.all')}</TabsTrigger>
            <TabsTrigger value="level1">{t('puttingTraining.history.tabs.level1')}</TabsTrigger>
            <TabsTrigger value="level2">{t('puttingTraining.history.tabs.level2')}</TabsTrigger>
            <TabsTrigger value="level3">{t('puttingTraining.history.tabs.level3')}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            {renderResultsSection(puttingResults)}
          </TabsContent>
          
          <TabsContent value="level1">
            {renderResultsSection(level1Results)}
          </TabsContent>
          
          <TabsContent value="level2">
            {renderResultsSection(level2Results)}
          </TabsContent>
          
          <TabsContent value="level3">
            {renderResultsSection(level3Results)}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PuttingHistoryTabs;
